import { Crown, Loader2, Power, PowerOff, ShieldCheck, User } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ncoder/ui';
import { IconActionButton } from '@/components/molecules/IconActionButton';
import { StatusBadge } from '@/components/atoms/org-admin/StatusBadge';
import { MemberAvatar } from '@/components/atoms/org-admin/MemberAvatar';
import { cn } from '@/lib/utils';
import type { ManagementLevel, OrgRole } from '@/services/org-admin';
import { isActiveMemberStatus } from './assignableMembers';
import { NO_PERMISSION_REASON } from './types';

const LEVEL_ICON: Record<ManagementLevel, typeof Crown> = {
  owner: Crown,
  admin: ShieldCheck,
  member: User,
};

// Anchor below the trigger (Radix's default "item-aligned" mode opens in an
// unpredictable direction/position) and scroll instead of overflowing once
// the list is taller than the viewport allows.
const SELECT_CONTENT_PROPS = {
  position: 'popper' as const,
  sideOffset: 4,
  className: 'max-h-60 min-w-[--radix-select-trigger-width] overflow-y-auto',
};

export interface MemberRowProps {
  memberId: string;
  userId: string;
  email: string;
  /** Derived from the assigned role; not independently editable here. */
  managementLevel: ManagementLevel | null;
  status: string;
  roleId: string | null;
  roleName: string | null;
  isSelf: boolean;
  /**
   * Why this member's role cannot be changed, or `undefined` when it can.
   *
   * Computed by the panel because it depends on the whole roster — whether this is the org's last
   * owner — which a single row cannot see. See `roleChangeBlockedReason`.
   */
  roleChangeBlockedReason?: string;
  roles: OrgRole[];
  canManage: boolean;
  isAssigningRole: boolean;
  isTogglingActive: boolean;
  onAssignRole: (roleId: string) => void;
  onToggleActive: () => void;
}

/**
 * The role control.
 *
 * It used to be a `Select` with `border-none` on a muted pill — which read as a status badge, not as
 * something clickable, so the panel's most-used action (reassigning a role) was undiscoverable. It
 * now looks like the control it is, and says what it does when it is unavailable rather than being
 * inertly greyed out.
 */
function RoleSelect({
  roleId,
  roleName,
  roles,
  status,
  canManage,
  isAssigningRole,
  onAssignRole,
  roleChangeBlockedReason,
  ariaLabel,
  className,
}: Pick<
  MemberRowProps,
  | 'roleId'
  | 'roleName'
  | 'roles'
  | 'status'
  | 'canManage'
  | 'isAssigningRole'
  | 'onAssignRole'
  | 'roleChangeBlockedReason'
> & {
  ariaLabel: string;
  className?: string;
}) {
  // A role the actor may not grant (an admin looking at an owner's row) is absent from `roles`, so
  // the Select would render an empty trigger over a real assignment. Show it as read-only text.
  const isGrantable = !roleId || roles.some((role) => role.id === roleId);
  /*
   * A deactivated seat's role cannot be changed.
   *
   * `updateOrgUserRole` requires an ACTIVE member and rejects anything else, so the dropdown was
   * offering a change that would always fail — and failing with an authorisation-shaped message, which
   * reads like the admin lacks permission rather than like the seat is switched off. Reactivate first
   * is the actual remedy, and the row's own power button is right there.
   */
  const isActive = isActiveMemberStatus(status);

  /*
   * Why this reads as text rather than a disabled dropdown.
   *
   * A disabled `<button>` is not focusable, so a tooltip on it is unreachable by keyboard — and the
   * reason is the entire value here ("the org must keep an owner" is not guessable from a greyed-out
   * control). Read-only text carries the reason in `title` for a pointer and in an `sr-only` sentence
   * for assistive tech, which is the same trade `IconActionButton` makes with `aria-disabled`.
   */
  const lockedReason = !canManage
    ? NO_PERMISSION_REASON
    : !isActive
      ? 'A deactivated member’s role cannot be changed — reactivate them first'
      : roleChangeBlockedReason;

  if (!canManage || !isGrantable || !isActive || roleChangeBlockedReason) {
    return (
      // `block`, not `inline-flex`: `text-overflow: ellipsis` has no effect on a flex container, so
      // the previous version clipped a long role name mid-word with no ellipsis to show it had.
      /*
       * The reason is a SIBLING of the truncating span, not a child.
       *
       * `.sr-only` is absolutely positioned, and inside `truncate` (`overflow: hidden`) with no
       * positioned ancestor it resolved against the table instead — landing at x≈1458 in a 1440px
       * viewport, which the sweep correctly flagged as clipped, unreachable content. As a sibling it
       * lays out inside the cell and stays reachable.
       */
      <>
        <span
          className={cn('oa-meta block truncate', className)}
          data-tone="strong"
          title={lockedReason ? `${roleName ?? '—'} — ${lockedReason}` : (roleName ?? undefined)}
        >
          {roleName ?? '—'}
        </span>
        {lockedReason && <span className="sr-only">{roleName ?? 'No role'} — {lockedReason}</span>}
      </>
    );
  }

  return (
    <Select value={roleId ?? ''} onValueChange={onAssignRole} disabled={isAssigningRole}>
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn(
          'h-7 w-full min-w-0 gap-1.5 rounded-md border-border/70 bg-background/50 px-2 text-xs font-medium',
          'transition-colors hover:border-border hover:bg-background',
          // A long role name ("Release Management & Compliance Reviewer") wrapped to two lines
          // inside the trigger and made that one row taller than every other row in the table.
          // Truncating keeps the row rhythm; the full name is still in the open dropdown.
          'whitespace-nowrap [&>span]:min-w-0 [&>span]:truncate',
          className,
        )}
      >
        {isAssigningRole ? (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Saving…
          </span>
        ) : (
          <SelectValue placeholder="No role" />
        )}
      </SelectTrigger>
      <SelectContent {...SELECT_CONTENT_PROPS}>
        {roles.map((role) => (
          <SelectItem key={role.id} value={role.id} className="text-xs">
            {role.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AuthorityLabel({ level }: { level: ManagementLevel | null }) {
  const Icon = (level && LEVEL_ICON[level]) ?? User;
  return (
    <span className="oa-meta inline-flex items-center gap-1.5 capitalize">
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {level ?? 'No role'}
    </span>
  );
}

function useToggleAction({
  isSelf,
  canManage,
  status,
  email,
}: Pick<MemberRowProps, 'isSelf' | 'canManage' | 'status' | 'email'>) {
  const isActive = status.toLowerCase() === 'active';
  return {
    isActive,
    // Named per member, not per action: a screen reader reading a roster of twenty rows otherwise
    // announces twenty buttons all called "Deactivate member", with nothing to tell them apart.
    label: isActive ? `Deactivate ${email}` : `Reactivate ${email}`,
    icon: isActive ? PowerOff : Power,
    tone: (isActive ? 'destructive' : 'success') as 'destructive' | 'success',
    disabled: isSelf || !canManage,
    disabledReason: isSelf
      ? 'You cannot deactivate your own account'
      : !canManage
        ? NO_PERMISSION_REASON
        : undefined,
  };
}

/** Table variant — used whenever the panel has room for columns. */
export function MemberRow(props: MemberRowProps) {
  const { email, userId, managementLevel, status, isSelf, isTogglingActive, onToggleActive } = props;
  const action = useToggleAction(props);

  return (
    <tr>
      <td>
        <div className="flex min-w-0 items-center gap-2.5">
          <MemberAvatar email={email} userId={userId} />
          <span className="truncate font-medium">{email}</span>
          {isSelf && <span className="oa-meta shrink-0">(you)</span>}
        </div>
      </td>
      <td>
        <StatusBadge status={status} />
      </td>
      <td>
        <AuthorityLabel level={managementLevel} />
      </td>
      <td>
        <RoleSelect {...props} ariaLabel={`Role for ${email}`} className="max-w-[180px]" />
      </td>
      <td>
        <div className="oa-row-actions flex justify-end">
          <IconActionButton
            label={action.label}
            icon={action.icon}
            tone={action.tone}
            size="sm"
            isPending={isTogglingActive}
            disabled={action.disabled}
            disabledReason={action.disabledReason}
            onClick={onToggleActive}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * Card variant — used when the panel is too narrow for five columns.
 *
 * The old row was a single flex line with a `shrink-0` action cluster pinned right, so below about
 * 520px of panel width the email truncated toward nothing while the controls kept their full size.
 * Stacking is the honest answer at that width; squeezing is not.
 */
export function MemberCard(props: MemberRowProps) {
  const { email, userId, managementLevel, status, isSelf, isTogglingActive, onToggleActive } = props;
  const action = useToggleAction(props);

  return (
    <div className="oa-card-row">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <MemberAvatar email={email} userId={userId} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium">{email}</span>
              {isSelf && <span className="oa-meta shrink-0">(you)</span>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <StatusBadge status={status} />
              <AuthorityLabel level={managementLevel} />
            </div>
          </div>
        </div>
        <IconActionButton
          label={action.label}
          icon={action.icon}
          tone={action.tone}
          size="sm"
          isPending={isTogglingActive}
          disabled={action.disabled}
          disabledReason={action.disabledReason}
          onClick={onToggleActive}
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="oa-micro shrink-0">Role</span>
        <RoleSelect {...props} ariaLabel={`Role for ${email}`} className="max-w-[220px]" />
      </div>
    </div>
  );
}
